import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CloudRain, Shield, Activity, Globe, Wifi, Radio, Zap, MapPin, ChevronRight, Sun, Wind, Cloud, Droplets, Thermometer, User, Bell, ChevronDown, ExternalLink, Crosshair, ArrowRight, Battery, Cpu, Leaf } from 'lucide-react';
import { WeatherData, LatLng, Contact, Alert, SafetyStatus, ViewState, NewsItem } from '../types';
import { generateLocalNews } from '../services/newsService';
import { getEffectiveAvatar } from '../services/contactService';
import { generateHumanSummary } from '../services/summaryService';
import { WeatherDetailModal } from './WeatherDetailModal';
import SurvivalCard from './SurvivalCard';
import MCPStatusIndicator from './MCPStatusIndicator';

interface DashboardProps {
    userProfile: Contact | null;
    weather: WeatherData | null;
    location: LatLng | null;
    hazards: Alert[];
    contacts: Contact[];
    onNavigate: (view: any, params?: any) => void;
    onSos: () => void;
    onNewsUpdate?: (news: NewsItem[]) => void;
}

const Dashboard: React.FC<DashboardProps> = ({
    userProfile,
    weather,
    location,
    hazards,
    contacts,
    onNavigate,
    onSos,
    onNewsUpdate
}) => {
    const riskLevel = hazards.length > 5 ? 'CRITICAL' : hazards.length > 2 ? 'ELEVATED' : 'NOMINAL';
    const accentColor = riskLevel === 'CRITICAL' ? 'var(--accent-danger)' : riskLevel === 'ELEVATED' ? 'var(--accent-alert)' : 'var(--accent-safe)';

    // Status State
    const [newsScope, setNewsScope] = useState<'local' | 'country' | 'world'>('local');
    const [newsFeed, setNewsFeed] = useState<NewsItem[]>([]);
    const [isLoadingNews, setIsLoadingNews] = useState(false);
    const [summaryText, setSummaryText] = useState("");
    const [currentTime, setCurrentTime] = useState(new Date());
    const [greeting, setGreeting] = useState('');
    const [showWeatherModal, setShowWeatherModal] = useState(false);

    // Live Clock & Greeting
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        const hour = new Date().getHours();
        setGreeting(hour < 12 ? 'Good Morning' : hour < 18 ? 'Good Afternoon' : 'Good Evening');
        return () => clearInterval(timer);
    }, []);

    // Generate Human Summary
    useEffect(() => {
        setSummaryText(generateHumanSummary(userProfile, weather, hazards, contacts));
    }, [userProfile, weather, hazards, contacts]);

    // Fetch News
    useEffect(() => {
        const fetchNews = async () => {
            if (!location) return;
            setIsLoadingNews(true);
            try {
                // Cinematic delay for realism
                await new Promise(resolve => setTimeout(resolve, 600));
                const news = await generateLocalNews(location, 'en', weather, hazards, newsScope);
                setNewsFeed(news);
                if (onNewsUpdate) onNewsUpdate(news);
            } catch (e) {
                console.error("News fetch failed", e);
            } finally {
                setIsLoadingNews(false);
            }
        };
        fetchNews();
    }, [newsScope, location, hazards]);

    // Helpers
    const getWeatherIcon = (condition: string = "") => {
        const c = condition.toLowerCase();
        if (c.includes('rain')) return <CloudRain className="w-full h-full text-blue-400" />;
        if (c.includes('cloud')) return <Cloud className="w-full h-full text-gray-400" />;
        if (c.includes('clear') || c.includes('sun')) return <Sun className="w-full h-full text-amber-400" />;
        return <Wind className="w-full h-full text-cyan-400" />;
    };

    // --- ANIMATIONS ---
    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 300, damping: 25 } }
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="h-full w-full overflow-y-auto text-white scrollbar-hide"
            style={{ backgroundColor: 'var(--bg-deepest)', fontFamily: 'var(--font-sans)' }}
        >
            {/* NATURE-TECH BACKGROUND */}
            <div className="fixed top-0 left-0 right-0 h-[400px] bg-gradient-to-b from-[var(--bg-deep)] to-transparent pointer-events-none z-0" />
            <div className="fixed -top-40 -right-40 w-96 h-96 rounded-full blur-[150px] opacity-10 pointer-events-none z-0 animate-breathe" style={{ background: 'radial-gradient(circle, var(--nature-forest) 0%, transparent 70%)' }} />
            <div className="fixed bottom-20 -left-20 w-64 h-64 rounded-full blur-[120px] opacity-10 pointer-events-none z-0 animate-leaf-float" style={{ background: 'radial-gradient(circle, var(--nature-sky) 0%, transparent 70%)' }} />

            <div className="relative z-10 px-5 pt-6 pb-32 flex flex-col gap-6">

                {/* --- HEADER: PROFILE & STATUS --- */}
                <motion.div variants={itemVariants} className="flex justify-between items-start">
                    <div>
                        <div className="text-xs font-semibold tracking-widest mb-2 uppercase" style={{ color: 'var(--text-muted)' }}>
                            {currentTime.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
                        </div>
                        <h1 className="text-3xl font-black tracking-tight leading-tight" style={{ color: 'var(--text-primary)' }}>
                            {greeting},<br />
                            <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(to right, var(--text-primary), var(--text-muted))' }}>
                                {userProfile?.name?.split(' ')[0] || 'Agent'}
                            </span>
                        </h1>
                    </div>

                    <button
                        onClick={() => onNavigate(ViewState.PROFILE)}
                        className="relative group cursor-pointer"
                        style={{ minWidth: '48px', minHeight: '48px' }}
                    >
                        <div className="w-14 h-14 rounded-3xl overflow-hidden shadow-lg transition-all duration-300 group-active:scale-95" style={{ backgroundColor: 'var(--bg-card)', border: '2px solid rgba(255,255,255,0.1)' }}>
                            <img src={getEffectiveAvatar(userProfile)} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center animate-soft-pulse" style={{ backgroundColor: 'var(--accent-safe)', border: '2px solid var(--bg-deepest)' }}>
                            <div className="w-2 h-2 bg-white rounded-full" />
                        </div>
                    </button>
                </motion.div>

                {/* --- MCP STATUS INDICATOR --- */}
                <motion.div variants={itemVariants}>
                    <MCPStatusIndicator />
                </motion.div>

                {/* --- BRIEFING CARD (Nature-Tech) --- */}
                <motion.div variants={itemVariants} className="relative">
                    <div className="absolute inset-0 rounded-3xl blur opacity-15" style={{ background: `linear-gradient(135deg, var(--nature-forest), var(--nature-sky))` }} />
                    <div className="relative backdrop-blur-xl rounded-3xl p-5 shadow-xl overflow-hidden group" style={{ backgroundColor: 'var(--bg-glass-strong)', border: '1px solid rgba(255,255,255,0.1)' }}>

                        {/* Organic shimmer on hover */}
                        <div className="absolute top-0 left-0 w-full h-1 opacity-0 group-hover:opacity-100 transition-opacity animate-shimmer" style={{ background: `linear-gradient(90deg, transparent, var(--accent-primary), transparent)` }} />

                        <div className="flex gap-4">
                            <div className="shrink-0 flex flex-col items-center gap-2 pt-1">
                                <div className="w-11 h-11 rounded-2xl flex items-center justify-center animate-breathe" style={{ backgroundColor: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.3)', color: 'var(--accent-primary)', boxShadow: '0 0 20px rgba(96,165,250,0.2)' }}>
                                    <Leaf size={20} />
                                </div>
                                <div className="h-full w-px" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }} />
                            </div>

                            <div className="flex-1 pb-2">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Daily Briefing</h3>
                                    <span className="text-xs font-bold px-3 py-1 rounded-xl" style={{
                                        backgroundColor: riskLevel === 'NOMINAL' ? 'rgba(16,185,129,0.15)' : 'rgba(248,113,113,0.15)',
                                        border: `1px solid ${riskLevel === 'NOMINAL' ? 'rgba(16,185,129,0.3)' : 'rgba(248,113,113,0.3)'}`,
                                        color: riskLevel === 'NOMINAL' ? 'var(--accent-safe)' : 'var(--accent-danger)'
                                    }}>
                                        {riskLevel} RISK
                                    </span>
                                </div>
                                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
                                    {summaryText || "Analyzing sector telemetry. Stand by for status update."}
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* --- MISSION CONTROL GRID --- */}
                <div className="grid grid-cols-2 gap-3">

                    {/* WEATHER WIDGET */}
                    <motion.div
                        variants={itemVariants}
                        onClick={() => setShowWeatherModal(true)}
                        className="col-span-1 rounded-3xl backdrop-blur p-5 flex flex-col justify-between h-44 relative group cursor-pointer transition-all duration-300 active:scale-95"
                        style={{ backgroundColor: 'var(--bg-glass)', border: '1px solid rgba(255,255,255,0.08)' }}
                        whileHover={{ scale: 1.02 }}
                    >
                        <div className="flex justify-between items-start">
                            <span className="text-xs uppercase font-semibold tracking-wider" style={{ color: 'var(--text-muted)' }}>Environment</span>
                            <ExternalLink size={14} className="transition-colors" style={{ color: 'var(--text-muted)' }} />
                        </div>

                        <div className="absolute right-2 top-10 w-20 h-20 opacity-20 blur-sm group-hover:blur-0 group-hover:scale-110 transition-all duration-500">
                            {getWeatherIcon(weather?.condition)}
                        </div>

                        <div>
                            <div className="text-5xl font-light tracking-tighter mb-1" style={{ color: 'var(--text-primary)' }}>
                                {Math.round(weather?.temp || 0)}°
                            </div>
                            <div className="text-sm font-medium truncate pr-4" style={{ color: 'var(--accent-primary)' }}>
                                {weather?.condition || 'Clear'}
                            </div>
                        </div>
                    </motion.div>

                    {/* SQUAD WIDGET */}
                    <motion.div
                        variants={itemVariants}
                        onClick={() => onNavigate(ViewState.CONTACTS)}
                        className="col-span-1 rounded-3xl backdrop-blur p-5 flex flex-col justify-between h-44 relative group cursor-pointer transition-all duration-300 active:scale-95"
                        style={{ backgroundColor: 'var(--bg-glass)', border: '1px solid rgba(255,255,255,0.08)' }}
                        whileHover={{ scale: 1.02 }}
                    >
                        <div className="flex justify-between items-start mb-2">
                            <span className="text-xs uppercase font-semibold tracking-wider" style={{ color: 'var(--text-muted)' }}>Team</span>
                            <div className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full animate-soft-pulse" style={{ backgroundColor: 'var(--accent-safe)' }} />
                                <span className="text-xs font-mono" style={{ color: 'var(--accent-safe)' }}>{contacts.filter(c => c.status === 'SAFE').length}/{contacts.length}</span>
                            </div>
                        </div>

                        <div className="flex -space-x-3 overflow-hidden py-2 pl-1">
                            {contacts.slice(0, 3).map((contact, i) => (
                                <div key={contact.id} className="relative w-11 h-11 rounded-full ring-2 z-10" style={{ borderColor: 'var(--bg-deepest)' }}>
                                    <img src={contact.avatarUrl} className="w-full h-full object-cover rounded-full" />
                                    {contact.status !== 'SAFE' && (
                                        <div className="absolute inset-0 rounded-full ring-2 animate-pulse" style={{ borderColor: 'var(--accent-danger)' }} />
                                    )}
                                </div>
                            ))}
                            {contacts.length > 3 && (
                                <div className="w-11 h-11 rounded-full ring-2 flex items-center justify-center text-xs font-bold z-0" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--bg-deepest)', color: 'var(--text-primary)' }}>
                                    +{contacts.length - 3}
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-1 text-xs mt-2 group-hover:text-white transition-colors" style={{ color: 'var(--text-muted)' }}>
                            Manage Team <ArrowRight size={12} />
                        </div>
                    </motion.div>

                    {/* MAP WIDGET */}
                    <motion.div
                        variants={itemVariants}
                        onClick={() => onNavigate(ViewState.COMMAND_CENTER)}
                        className="col-span-2 rounded-3xl p-0.5 relative cursor-pointer group overflow-hidden h-28"
                        style={{ backgroundColor: 'var(--bg-glass)', border: '1px solid rgba(255,255,255,0.08)' }}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <div className="absolute inset-0 bg-[url('https://api.mapbox.com/styles/v1/mapbox/dark-v10/static/0,0,0,0,0/600x200')] bg-cover opacity-30 grayscale group-hover:grayscale-0 transition-all duration-700" />
                        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />

                        <div className="relative h-full flex flex-col justify-center px-6 z-10">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-2xl backdrop-blur flex items-center justify-center transition-transform group-hover:scale-110" style={{ backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)' }}>
                                    <MapPin size={22} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-base mb-1" style={{ color: 'var(--text-primary)' }}>Tactical Map</h3>
                                    <p className="text-xs uppercase tracking-widest transition-colors" style={{ color: 'var(--text-muted)' }}>
                                        Tap to Navigate
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* PULSE WIDGET */}
                    <motion.div
                        variants={itemVariants}
                        onClick={() => onNavigate(ViewState.PULSE)}
                        className="col-span-1 rounded-3xl backdrop-blur p-5 relative overflow-hidden group cursor-pointer transition-all duration-300 active:scale-95"
                        style={{
                            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, var(--bg-glass) 100%)',
                            border: '1px solid rgba(255,255,255,0.08)'
                        }}
                        whileHover={{ scale: 1.02 }}
                    >
                        <div className="absolute -right-4 -bottom-4 w-24 h-24 rounded-full blur-[50px] opacity-20 group-hover:opacity-40 transition-opacity" style={{ backgroundColor: 'var(--tech-glow)' }} />
                        <Activity className="mb-3" size={26} style={{ color: 'var(--tech-glow)' }} />
                        <h3 className="font-bold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>Pulse Net</h3>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>System Nominal</p>
                    </motion.div>

                    {/* SOS BUTTON */}
                    <motion.div
                        variants={itemVariants}
                        onClick={onSos}
                        className="col-span-1 rounded-3xl p-5 relative overflow-hidden group cursor-pointer transition-all duration-300 active:scale-95 flex flex-col justify-center"
                        style={{ backgroundColor: 'var(--accent-danger)' }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.96 }}
                    >
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                        <div className="relative z-10 flex items-center justify-between">
                            <span className="font-black text-xl tracking-widest" style={{ color: 'white' }}>SOS</span>
                            <Radio className="animate-pulse" size={22} style={{ color: 'white' }} />
                        </div>
                        <p className="relative z-10 text-xs mt-2 uppercase font-bold" style={{ color: 'rgba(255,255,255,0.9)' }}>Emergency Only</p>
                    </motion.div>

                </div>

                {/* --- CRITICAL ALERTS --- */}
                {hazards.some(h => h.severity === 'critical') && (
                    <motion.div variants={itemVariants} className="mt-2">
                        {hazards.filter(h => h.severity === 'critical').slice(0, 1).map(h => (
                            <SurvivalCard key={h.id} title={h.type} severity="critical" tips={h.survivalTips || []} />
                        ))}
                    </motion.div>
                )}

                {/* --- INTEL MINI FEED (Timeline) --- */}
                <motion.div variants={itemVariants} className="bg-[#1E293B]/20 rounded-3xl border border-white/5 p-5 backdrop-blur-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Shield size={14} className="text-[var(--neon-cyan)]" />
                            <h3 className="text-sm font-bold text-white">Live Intel</h3>
                        </div>
                        <button
                            onClick={() => onNavigate(ViewState.INTEL)}
                            className="text-[10px] font-bold text-slate-500 hover:text-white transition-colors"
                        >
                            VIEW ALL
                        </button>
                    </div>

                    <div className="space-y-0 relative">
                        {/* Connecting Line */}
                        <div className="absolute left-[5px] top-2 bottom-4 w-px bg-white/10" />

                        {(newsFeed.length > 0 ? newsFeed.slice(0, 3) : hazards.slice(0, 3)).map((item: any, i) => (
                            <div key={item.id || i} className="pl-5 py-2 relative group">
                                <div className={`absolute left-0 top-3.5 w-2.5 h-2.5 rounded-full border-2 border-[#0F172A] ${item.severity === 'critical' ? 'bg-red-500' : 'bg-slate-600 group-hover:bg-[var(--neon-cyan)]'} transition-colors z-10`} />

                                <div className="flex justify-between items-start">
                                    <h4 className="text-xs font-medium text-slate-200 line-clamp-1 group-hover:text-[var(--neon-cyan)] transition-colors">
                                        {item.headline || item.type}
                                    </h4>
                                    <span className="text-[9px] font-mono text-slate-500 whitespace-nowrap ml-2">
                                        {item.time?.split(' ')[0] || 'NOW'}
                                    </span>
                                </div>
                                <p className="text-[10px] text-slate-400 line-clamp-1 mt-0.5">
                                    {item.location}
                                </p>
                            </div>
                        ))}

                        {newsFeed.length === 0 && hazards.length === 0 && (
                            <div className="text-center py-6">
                                <p className="text-xs text-slate-500">No active intelligence in sector.</p>
                            </div>
                        )}
                    </div>
                </motion.div>

            </div>

            {/* MODALS */}
            <WeatherDetailModal
                isOpen={showWeatherModal}
                onClose={() => setShowWeatherModal(false)}
                weatherData={weather}
            />
        </motion.div>
    );
};

export default Dashboard;
